import { Box, Typography } from "@mui/material";
import React, { useState } from "react";
import bebe from "../assets/newImg/bebe.png";
import SEO from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const STATS = [
  { value: "50 €", label: "Consultation pédiatrique" },
  { value: "30 min", label: "Durée moyenne de la séance" },
  { value: "1 / 4", label: "Nourrissons consultés dans le premier mois" },
  { value: "2 / 3", label: "Bébés avec au moins une tension à la naissance" },
];

const INDICATIONS = [
  {
    title: "Plagiocéphalie",
    desc: "Aplatissement du crâne dû à une position préférentielle ou à une tension des membranes crâniennes.",
  },
  {
    title: "Coliques du nourrisson",
    desc: "Pleurs inconsolables, agitation, ballonnements  souvent soulagés dès la première séance.",
  },
  {
    title: "Torticolis congénital",
    desc: "Préférence de rotation de la tête, souvent liée à la position utérine ou à l'accouchement.",
  },
  {
    title: "Reflux & régurgitations",
    desc: "Travail viscéral doux sur le cardia et le diaphragme pour améliorer la vidange gastrique.",
  },
  {
    title: "Troubles du sommeil",
    desc: "Difficultés d'endormissement, réveils fréquents, inconfort positionnel.",
  },
  {
    title: "Suivi post-accouchement",
    desc: "Bilan systématique recommandé dans les 6 semaines suivant la naissance, quelle que soit la voie d'accouchement.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Échange avec les parents",
    desc: "Le premier temps est dédié à l'anamnèse : déroulement de la grossesse, type d'accouchement, comportements du bébé au quotidien (sommeil, alimentation, pleurs).",
  },
  {
    num: "02",
    title: "Observation & bilan",
    desc: "Observation de la posture, des mobilités crâniennes et rachidiennes, du tonus musculaire. Tout est réalisé de manière douce, souvent pendant que le bébé dort.",
  },
  {
    num: "03",
    title: "Soin ostéopathique",
    desc: "Techniques crâniennes, tissulaires et viscérales adaptées au nourrisson. Les pressions exercées sont légères  équivalentes au poids d'une pièce de monnaie.",
  },
  {
    num: "04",
    title: "Conseils & exercices",
    desc: "Conseils sur la posture, le portage, les positions de sommeil. Des exercices simples peuvent être montrés aux parents pour prolonger l'effet de la séance.",
  },
];

const NourStep = ({ step, isLast }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      onClick={() => setOpen((o) => !o)}
      sx={{
        cursor: "pointer",
        userSelect: "none",
        display: "grid",
        gridTemplateColumns: "48px 1fr auto",
        gap: "1.25rem",
        alignItems: "flex-start",
        py: "1.25rem",
        borderBottom: isLast ? "none" : `1px solid ${C.border}`,
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: F.display,
          fontSize: "1.6rem",
          fontWeight: 300,
          color: C.accent,
          lineHeight: 1,
          pt: ".1rem",
        }}
      >
        {step.num}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: ".92rem",
            fontWeight: 600,
            color: C.text,
            pt: ".1rem",
          }}
        >
          {step.title}
        </Typography>
        <Box
          sx={{
            maxHeight: open ? "200px" : 0,
            overflow: "hidden",
            transition: "max-height .3s ease",
            fontSize: ".82rem",
            color: C.textMuted,
            lineHeight: 1.7,
            pb: open ? "1rem" : 0,
          }}
        >
          {step.desc}
        </Box>
      </Box>
      <Box
        component="span"
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          border: open ? `1.5px solid ${C.accent}` : `1.5px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: open ? "#fff" : C.textMuted,
          background: open ? C.accent : "transparent",
          fontSize: ".8rem",
          fontWeight: 300,
          transition: `all ${trans}`,
          flexShrink: 0,
          mt: ".1rem",
          transform: open ? "rotate(45deg)" : "none",
        }}
      >
        +
      </Box>
    </Box>
  );
};

const Nourrissons = () => (
  <>
    <SEO
      title="Ostéopathie nourrissons & bébés  Balma"
      description="Consultations ostéopathiques spécialisées pour nourrissons et bébés. Torticolis, coliques, plagiocéphalie, difficultés d'allaitement. À Balma et Castelnau-d'Estrétefonds."
      path="/pathologies/nourrissons"
      schema={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: "https://www.balma-osteopathe.fr/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pathologies",
            item: "https://www.balma-osteopathe.fr/pathologies",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Nourrissons",
            item: "https://www.balma-osteopathe.fr/pathologies/nourrissons",
          },
        ],
      }}
    />
    <Box component="section" sx={SX.pageHeroSection}>
      <Box
        sx={{
          ...SX.pageHeroInner,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 340px" },
          gap: { xs: "2rem", md: "4rem" },
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Nourrissons &amp; pédiatrie
          </Box>
          <Typography variant="h1" component="h1" sx={SX.displayTitle}>
            Le tout-petit, <em>en toute douceur.</em>
          </Typography>
          <Typography sx={SX.pageHeroSub}>
            Dès les premiers jours de vie, l'ostéopathie peut accompagner votre
            bébé. Les techniques utilisées sont entièrement adaptées à leur
            fragilité et à leur développement.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: "1.5rem", md: "2rem" },
              flexWrap: "wrap",
              mt: "2rem",
              pt: "2rem",
              borderTop: `1px solid ${C.border}`,
            }}
          >
            {STATS.map((s) => (
              <Box
                key={s.label}
                sx={{ display: "flex", flexDirection: "column", gap: ".2rem" }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: F.display,
                    fontSize: "1.7rem",
                    fontWeight: 400,
                    color: C.accent,
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: ".72rem",
                    color: C.textMuted,
                    lineHeight: 1.4,
                    maxWidth: "100px",
                  }}
                >
                  {s.label}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: { xs: "220px", md: "360px" },
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.md,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            component="img"
            src={bebe}
            alt="Ostéopathie pour nourrissons et bébés"
            sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 25%" }}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Indications ──────────────────────────── */}
    <Box component="section" sx={{ py: "5.5rem", background: C.bgAlt }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Indications
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Pourquoi consulter <em>pour bébé ?</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: "1.25rem",
          }}
        >
          {INDICATIONS.map((ind) => (
            <Box
              key={ind.title}
              sx={{
                background: C.surface,
                borderRadius: R.lg,
                padding: "1.5rem",
                border: `1px solid ${C.border}`,
                transition: `box-shadow ${trans}`,
                "&:hover": { boxShadow: S.md },
              }}
            >
              <Typography
                sx={{
                  fontSize: ".9rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".5rem",
                }}
              >
                {ind.title}
              </Typography>
              <Typography
                sx={{ fontSize: ".8rem", color: C.textMuted, lineHeight: 1.65 }}
              >
                {ind.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Déroulement ──────────────────────────── */}
    <Box component="section" sx={{ py: "6rem" }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
          gap: { xs: "3rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Déroulement
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            30 minutes, <em>douceur d'abord.</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".9rem", color: C.textMuted, lineHeight: 1.8 }}
          >
            La séance nourrisson dure entre 20 et 30 minutes. Elle se déroule
            dans un espace calme, à votre rythme. Le bébé peut être nourri,
            endormi ou éveillé cela n'influe pas sur la qualité du soin.
          </Typography>
        </Box>
        <Box>
          {STEPS.map((s, i) => (
            <NourStep key={s.num} step={s} isLast={i === STEPS.length - 1} />
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Approche Maxime ──────────────────────── */}
    <Box
      component="section"
      sx={{ py: { xs: "3rem", md: "5rem" }, background: C.surface }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: "2rem", md: "4rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Notre approche
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            Maman et bébé, <em>ensemble.</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".88rem", color: C.textMuted, lineHeight: 1.8 }}
          >
            J'aime traiter la maman en même temps que le bébé lors de la
            première consultation. Ce lien fort entre les deux facilite la mise
            en confiance : un bébé calme, c'est aussi une maman rassurée.
          </Typography>
          <Typography
            sx={{
              fontSize: ".88rem",
              color: C.textMuted,
              lineHeight: 1.8,
              mt: "1rem",
            }}
          >
            Il n'y a pas de « bon moment » imposé le meilleur moment, c'est
            quand bébé et les parents sont prêts.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {[
            {
              icon: "🫶",
              title: "Naissance par ventouses ou forceps",
              desc: "Ces instruments exercent des tensions sur le crâne du nouveau-né. Libérer ces restrictions peut, par exemple, soulager des difficultés d'allaitement ou des maux de tête du nourrisson.",
            },
            {
              icon: "🌀",
              title: "Naissance par césarienne ou en siège",
              desc: "La position utérine ou le mode d'extraction peuvent laisser des tensions dans les structures crâniennes et rachidiennes du bébé.",
            },
            {
              icon: "⚖️",
              title: "Bilan systématique recommandé",
              desc: "Même en l'absence de signe clinique, un bilan ostéopathique dans les premières semaines permet de prévenir des tensions qui pourraient se manifester plus tard.",
            },
          ].map((item) => (
            <Box
              key={item.title}
              sx={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: R.base,
                px: "1.25rem",
                py: "1rem",
                display: "flex",
                gap: ".75rem",
                alignItems: "flex-start",
              }}
            >
              <Box
                component="span"
                sx={{ fontSize: "1.2rem", flexShrink: 0, mt: ".05rem" }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".85rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".2rem",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".78rem",
                    color: C.textMuted,
                    lineHeight: 1.6,
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── CTA ──────────────────────────────────── */}
    <Box
      component="section"
      sx={{ background: C.dark, py: "5.5rem", textAlign: "center" }}
    >
      <Box
        sx={{ maxWidth: "600px", mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontFamily: F.display,
            fontSize: "clamp(2rem, 3.5vw, 3rem)",
            fontWeight: 300,
            color: "#fff",
            mb: "1rem",
            lineHeight: 1.2,
            "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
          }}
        >
          Quand bébé est <em>prêt.</em>
        </Typography>
        <Typography
          sx={{
            fontSize: ".88rem",
            color: "rgba(255,255,255,.5)",
            mb: "2rem",
            lineHeight: 1.7,
          }}
        >
          Le plus tôt est le mieux, mais il n'y a pas de délai imposé. Je vous
          reçois dès que vous êtes prêts, vous et votre bébé.
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Box
            component="a"
            href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
            target="_blank"
            rel="noreferrer"
            sx={SX.btnAccent}
          >
            Prendre rendez-vous
          </Box>
          <Box component="a" href="tel:0695788983" sx={SX.btnOutlineWhite}>
            06 95 78 89 83
          </Box>
        </Box>
      </Box>
    </Box>
  </>
);

export default Nourrissons;
